import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import {
  type NonEmptyArray,
  type TableWithReportsRequestBody,
  type TableWithReportsResponseBody,
  nonEmptyArray,
} from '@ipfabric/utils';
import { extractRowMetaInfo } from 'components/ApiTable/helpers/extractRowMetaInfo';
import { getVisibleColumnsDependencies } from 'components/ApiTable/helpers/getVisibleColumnsDependencies';
import type { ApiTableCommandFactory } from 'components/ApiTable/model/ApiTableModel';
import { selectTableId } from 'components/ApiTable/model/selectors/selectTableId';
import { selectTableRequestPayloadBase } from 'components/ApiTable/model/selectors/selectTableRequestPayloadBase';
import { identity, pipe } from 'fp-ts/function';

interface LoadTableRowsCommand {
  start: number;
  limit: number;
}

/** Backend currently represents "unknown rows" by this particular number. */
const API_UNKNOWN_ROW_COUNT_REPRESENTATION = 100000000;

export const loadTableRows: ApiTableCommandFactory<LoadTableRowsCommand> =
  (cmdContext) => async (command) => {
    let replacedByNewerDataRequest = false;
    try {
      const { endpoint, rowIdField } = cmdContext.getState();
      const { dataRequestNumber } = cmdContext.getTableModel();
      const tableId = selectTableId(cmdContext.getState());
      const { columnVisibility } = cmdContext.getTableModel().settings;
      const columnsToLoad = getVisibleColumnsDependencies(
        columnVisibility,
        endpoint,
        rowIdField,
      );
      if (!nonEmptyArray.isNonEmpty(columnsToLoad)) {
        stumble('Trying to load data for empty column set');
        return;
      }
      const requestPayload: TableWithReportsRequestBody<any> = {
        ...selectTableRequestPayloadBase(cmdContext.getState()),
        // Cast since TableColumn<any> results in NonEmptyArray<string | number | symbol>
        columns: columnsToLoad as NonEmptyArray<string>,
        pagination: {
          limit: command.limit,
          start: command.start,
        },
        reports: cmdContext.getState().enableTableReports ? tableId : undefined,
      };
      cmdContext.updateState((draft) => {
        draft.lastLoadColumnVisibility = columnVisibility;
      });
      const result = await cmdContext.api.request<
        TableWithReportsResponseBody<any>,
        TableWithReportsRequestBody<any>
      >({
        url: endpoint.apiEndpoint,
        method: 'POST',
        data: requestPayload,
      });
      if (cmdContext.getTableModel().dataRequestNumber !== dataRequestNumber) {
        replacedByNewerDataRequest = true;
        return;
      }
      cmdContext.updateState((draft) => {
        const { count } = result._meta;

        if (count === API_UNKNOWN_ROW_COUNT_REPRESENTATION) {
          draft.rowCount =
            command.start +
            result.data.length +
            // Until data are coming, pretend there is one more row to trigger lazy load once we scroll here.
            (result.data.length > 0 ? 1 : 0);
          draft.rowCountMore = result.data.length > 0;
        } else {
          draft.rowCount = count;
          draft.rowCountMore = false;
        }
        result.data.forEach((row, index) => {
          draft.data[index + command.start] = pipe(
            row,
            extractRowMetaInfo,
            cmdContext.getState().patchRow ?? identity,
          );
        });
        draft.lastRequestPayload = requestPayload;
      });
    } finally {
      // When rowCount is not filled - either from unexpected exception or premature return,
      // it should be set to 0 to stop loader.
      // Except the case when different data were requested in the meantime.
      if (
        !replacedByNewerDataRequest &&
        cmdContext.getState().rowCount === null
      ) {
        cmdContext.updateState((draft) => {
          draft.rowCount = 0;
        });
      }
    }
  };
